/**
 * Allys Agents Section
 * 
 * Sophisticated agents showcase with large typographic name behind image.
 * Features interactive flip cards with contact options and meeting scheduling.
 * Perfect for highlighting real estate agents with modern, editorial design.
 *
 * Agents can be provided explicitly, derived from listing data, or fall back
 * to the default hardcoded team members.
 */

'use client';

import { memo, useMemo } from 'react';
import { AgentsSection as CoreAgentsSection, FlipAgentCard, extractAgentsFromListings, type Agent } from '../../../../src/components/agents';
import { useTenantOptional } from '../../../../src/contexts/TenantContext';

interface ListingAgentSource {
  listingAgent: {
    id: string;
    name: string;
    email: string;
    avatar: string | null;
    jobTitle: string | null;
  } | null;
}

/** Default agents shown when no listing data is available */
const DEFAULT_AGENTS: Agent[] = [
  {
    id: '1',
    name: 'Yalda Sheri',
    image: 'https://media.routeroyal.com/XmN50nxBvldetj3mYWesFTu4Ct4o0aAFOhdEVbShzEA/raw:1/czM6Ly9jb2xsYXBzaWJsZS1wdXJzZS1kbW9rLWRubDd4cy9yb3V0ZXJveWFsL3JveWFsZS9zdGF0aWMvZDBlNjE1ZmI5MmY1MzQwMGE5YWI5YmRlMTY2NjcyZWU5MDQ0YmRhODQyY2MwMzU3NjdmMjRhMjA4YWJiZGY3NS5wbmc.png',
    title: 'Senior Property Consultant',
    bio: 'Specialising in luxury residential properties across Mauritius with over 10 years of experience.',
    phone: '+230 5258 4239',
    email: 'yalda@allys.mu',
    properties: 127,
  },
  {
    id: '2',
    name: 'Junaid Nuzeebun',
    image: 'https://media.routeroyal.com/XQfAbSbLOraw6YGU1hnBjNvIzTiAW_3tXdQLmgpuBB8/raw:1/czM6Ly9jb2xsYXBzaWJsZS1wdXJzZS1kbW9rLWRubDd4cy9yb3V0ZXJveWFsL3JveWFsZS9zdGF0aWMvYThkYjA4YTM4MzMyYjczMDQzMWZkYTQ4MjA2MjliZDQ4ZGRiYjlkMTZlMTVjMDMxNzU2NjhlNTViNjY2NDYxZC5wbmc.png',
    title: 'Property Development Specialist',
    bio: 'Expert in commercial and residential developments with a focus on investment opportunities.',
    phone: '+230 5258 4240',
    email: 'junaid@allys.mu',
    properties: 94,
  },
];

export interface AllysAgentsSectionProps {
  /**
   * Explicit array of agents to display (takes highest priority)
   */
  agents?: Agent[];

  /**
   * Listings from which to derive agents. Each listing's `listingAgent`
   * is extracted and de-duplicated. Falls back to DEFAULT_AGENTS when
   * neither `agents` nor `listings` yield results.
   */
  listings?: ListingAgentSource[];
  
  /**
   * Section title
   */
  title?: string;
  
  /**
   * Section subtitle
   */
  subtitle?: string;
  
  /**
   * Number of columns
   * @default 3
   */
  columns?: 1 | 2 | 3 | 4;
  
  /**
   * Messages for translations
   */
  messages?: Record<string, unknown>;
  
  /**
   * Current locale
   */
  locale?: 'en' | 'es' | 'fr';
}

const safeGetString = (obj: unknown, defaultValue: string): string => {
  if (typeof obj === 'string') return obj;
  return defaultValue;
};

/**
 * Allys Agents Section Component
 */
export const AllysAgentsSection = memo(function AllysAgentsSection({
  agents,
  listings,
  title,
  subtitle,
  columns = 2,
  messages,
  locale: _locale
}: AllysAgentsSectionProps) {
  const resolvedAgents = useMemo(() => {
    if (agents && agents.length > 0) return agents;
    if (listings && listings.length > 0) {
      const derived = extractAgentsFromListings(listings);
      if (derived.length > 0) return derived;
    }
    return DEFAULT_AGENTS;
  }, [agents, listings]);
  // Get tenant context for organisation ID
  const tenant = useTenantOptional();
  const organisationId = tenant?.organisationId || '';
  
  // Get translations from messages
  const agentsMessages = messages?.agents as Record<string, unknown> | undefined;
  const displayTitle = title || safeGetString(agentsMessages?.title, 'Meet Our Expert Agents');
  const displaySubtitle = subtitle || safeGetString(agentsMessages?.subtitle, 'Dedicated professionals ready to help you find your perfect property');
  const propertiesText = safeGetString(agentsMessages?.properties, 'Properties');
  
  // Messages for FlipAgentCard
  const cardMessages = {
    contact: safeGetString(agentsMessages?.contact, 'Contact'),
    call: safeGetString(agentsMessages?.call, 'Call'),
    email: safeGetString(agentsMessages?.email, 'Email'),
    scheduleMeeting: safeGetString(agentsMessages?.scheduleMeeting, 'Schedule Meeting'),
    properties: propertiesText,
    backToProfile: safeGetString(agentsMessages?.backToProfile, 'Back to Profile'),
  };
  
  return (
    <section className="py-14 sm:py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        {(displayTitle || displaySubtitle) && (
          <div className="text-center mb-10 sm:mb-16 lg:mb-20">
            {displayTitle && (
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 tracking-tight font-inter">
                {displayTitle}
              </h2>
            )}
            {displaySubtitle && (
              <p className="text-lg text-gray-600 max-w-2xl mx-auto font-inter">
                {displaySubtitle}
              </p>
            )}
          </div>
        )}

        {/* Agents Grid - Now with FlipAgentCard for interactive contact */}
        <CoreAgentsSection
          agents={resolvedAgents}
          columns={columns}
          gap="lg"
          renderAgent={(agent, index) => (
            <FlipAgentCard 
              agent={agent} 
              index={index} 
              organisationId={organisationId}
              messages={cardMessages}
            />
          )}
        />
      </div>
    </section>
  );
});

// Legacy AllysAgentCard removed - now using FlipAgentCard from core components
// The FlipAgentCard provides enhanced interactivity with:
// - 3D flip animation on contact button click
// - Call button (opens phone dialer)
// - Email button (opens email client with prepopulated details)
// - Schedule Meeting button (opens cal.com style modal)

